import { useCallback, useEffect, useState, FormEvent } from "react";
import { ITag, MessageBarType } from "@fluentui/react";
import { EmailListItem } from "../models/EmailListResponse";
import { AppPage } from "../models/AppPage";
import { IHub } from "../models/Hub";
import { useAppState } from "../store/AppProvider";
import { EmailListItemWithSelectedStatus } from "../components/fileMultipleEmail/FileMultipleEmailComponent";
import { Logger } from "../services/Logger";
import { EmailApiService } from "../services/NewformaApi/EmailApiService";
import { Authenticator } from "../services/Authenticator";
import { TranslateFunction } from "react-localize-redux";
import { ToastMessage } from "../models/ToastMessage";
import { useFileEmailHelpers } from "./useFileEmailsHelpers";
import { pluralizeSentence } from "../utils/helpers";
import { useAppService } from "../services/AppServiceProvider";

const moveItemToEnd = (arr: string[], str: string) => [
    ...arr.filter((item) => item !== str),
    ...arr.filter((item) => item === str),
];

export interface UseFileMultipleEmailComponentProps {
    actions: {
        onExpiredSession: () => void;
        onShowToast: (message: ToastMessage | null, type: MessageBarType) => void;
        onSetNavigationPage: (page: AppPage) => void;
        showProgress: (message: string | null) => void;
        translate: TranslateFunction;
    };
    states: {
        deleteEmailAfterFiling: boolean;
    };
    services: {
        logger: Logger;
        emailApiService: EmailApiService;
        authenticator: Authenticator;
    };
}

// TODO: 45422: Revisit implementation of useFileEmail hook
const useFileEmails = (props: UseFileMultipleEmailComponentProps) => {
    const { actions, services, states } = props;

    const { onSetNavigationPage, translate, showProgress, onShowToast } = actions;
    const { emailApiService, logger } = services;
    const { deleteEmailAfterFiling } = states;

    const [isLoadingEmails, setIsLoadingEmails] = useState(false);
    const [isLoadingMoreEmails, setIsLoadingMoreEmails] = useState(false);
    const [isLoadingFolderName, setIsLoadingFolderName] = useState(false);
    const [isFiling, setIsFiling] = useState(false);
    const [loadedEmails, setLoadedEmails] = useState<EmailListItem[]>([]);
    const [emails, setEmails] = useState<EmailListItemWithSelectedStatus[]>([]);
    const [emailOffsetToken, setEmailOffsetToken] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string | null>(null);
    const [folderName, setFolderName] = useState<string>("");
    const [folderId, setFolderId] = useState<string | null>(null);
    const [currentEmailItem, setCurrentEmailItem] = useState<EmailListItem | null>(null);

    const { selectedHub, selectedProject, supportedAddinItems, mailboxItem } = useAppState().appState;

    const {
        services: { msGraphApiService },
    } = useAppService();

    const { handleApiError, moveSelectedEmailToTopOfList, selectedEmailsIds, getFilingMessage } =
        useFileEmailHelpers(props);

    useEffect(() => {
        onSetNavigationPage(AppPage.FileMultipleEmail);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const newFolderId = await msGraphApiService.getCurrentFolderId();
                const newCurrentEmailItem = await msGraphApiService.getCurrentMessageAsEmailListItem();
                const newFolderName = await msGraphApiService.getFolderName(newFolderId);

                setFolderId(newFolderId);
                setCurrentEmailItem(newCurrentEmailItem);
                setFolderName(newFolderName);
            } catch (error) {
                handleApiError(error, translate("SHARED.ERRORS.LOADING_PROJECTS_GENERIC") as string);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            setIsLoadingFolderName(true);
            setEmails([]);
            setLoadedEmails([]);

            try {
                await loadEmails();
            } catch (error) {
                logger.error("error loading outlook context");
                handleApiError(error, translate("SHARED.ERRORS.LOADING_PROJECTS_GENERIC") as string);
            } finally {
                setIsLoadingFolderName(false);
            }
        })();
    }, [folderId]);

    useEffect(() => {
        (async () => {
            logger.info("Selected Email Changed");

            const newFolderId = await msGraphApiService.getCurrentFolderId();
            if (folderId !== newFolderId) {
                // Special case in old outlook desktop app, where addins do not re-render on folderChange, we must manually trigger this.
                setFolderId(newFolderId);

                const newFolderName = await msGraphApiService.getFolderName(newFolderId);
                setFolderName(newFolderName);
                return;
            }

            const newCurrentEmailItem = await msGraphApiService.getCurrentMessageAsEmailListItem();

            const mappedItems: EmailListItemWithSelectedStatus[] = loadedEmails.map((item) => ({
                ...item,
                isSelected:
                    item.id === currentEmailItem?.id
                        ? false
                        : emails.find((x) => x.id === item.id)?.isSelected || false,
            }));
            const newEmails = moveSelectedEmailToTopOfList(mappedItems, newCurrentEmailItem);
            setCurrentEmailItem(newCurrentEmailItem);
            setEmails(newEmails);
        })();
    }, [mailboxItem, loadedEmails]);

    const onSelectAllChanged = (event?: FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean): void => {
        if (checked === undefined) {
            return;
        }

        const emailsDeepCopy: EmailListItemWithSelectedStatus[] = JSON.parse(JSON.stringify(emails));

        if (checked) {
            const emailIds: string[] = [];
            const checkedEmails = emailsDeepCopy.map((email) => {
                emailIds.push(email.id);
                return { ...email, isSelected: true };
            });
            setEmails(checkedEmails);
            return;
        }

        const uncheckedEmails = emailsDeepCopy.map((email) => ({ ...email, isSelected: false }));
        setEmails(uncheckedEmails);
    };

    const loadEmails = useCallback(
        async (
            isLoadingMore: boolean = false,
            isSearching: boolean = false,
            searchValue: string | null = null,
            offsetToken: string | null = null
        ): Promise<void> => {
            logger.info("Calling loadEmails:");

            setIsLoadingEmails(!isLoadingMore);
            setIsLoadingMoreEmails(isLoadingMore);

            try {
                const emailResponse = await msGraphApiService.getFolderEmails(
                    folderId || (await msGraphApiService.getCurrentFolderId()),
                    offsetToken,
                    searchValue
                );

                if (isSearching) {
                    setEmails(
                        emailResponse.items.map((item) => ({
                            ...item,
                            isSelected: false,
                        }))
                    );
                } else {
                    const newCurrentEmailItem = await msGraphApiService.getCurrentMessageAsEmailListItem();

                    const mappedEmails = moveSelectedEmailToTopOfList(
                        emailResponse.items.map((item) => ({
                            ...item,
                            isSelected: false,
                        })),
                        newCurrentEmailItem
                    );

                    if (isLoadingMore) {
                        setEmails((prevEmails) => [
                            ...prevEmails,
                            ...mappedEmails.filter(
                                (newEmail) => !prevEmails.some((prevEmail) => prevEmail.id === newEmail.id)
                            ),
                        ]);
                    } else {
                        setEmails(mappedEmails);
                    }
                }

                setLoadedEmails((prevLoadedEmails) => {
                    const existingEmailIds = new Set(prevLoadedEmails.map((email) => email.id));
                    const newEmails = emailResponse.items.filter((email) => !existingEmailIds.has(email.id));
                    return [...prevLoadedEmails, ...newEmails];
                });
                setEmailOffsetToken(emailResponse.paging.offsetToken);
            } catch (error) {
                logger.error("error loading emails");
            } finally {
                setIsLoadingEmails(false);
                setIsLoadingMoreEmails(false);
            }
        },
        [folderId]
    );

    const onEmailSearchClear = async (): Promise<void> => {
        setEmails([]);
        setSearchTerm(null);
        setEmailOffsetToken(null);
        setLoadedEmails([]);

        await loadEmails(false, true);
    };

    const onEmailSearch = async (searchValue?: string): Promise<void> => {
        if (!searchValue) {
            await onEmailSearchClear();
            return;
        }

        setSearchTerm(searchValue);
        setIsLoadingEmails(true);
        setEmailOffsetToken(null);
        setEmails([]);
        setLoadedEmails([]);

        await loadEmails(false, true, searchValue);
    };

    const onEmailSelectionStateChanged = (id: string, checked: boolean): void => {
        const emailsDeepCopy: EmailListItemWithSelectedStatus[] = JSON.parse(JSON.stringify(emails));
        const updatedEmails = emailsDeepCopy.map((email) => {
            if (email.id === id) {
                return { ...email, isSelected: checked };
            }
            return email;
        });

        setEmails(updatedEmails);
    };

    const showToastMessage = useCallback(
        (failedIds: string[], numberOfEmailsToFile: number, projectName: string) => {
            let toastMessage;
            if (failedIds.length) {
                const errorMessageTemplate = translate("FILE_MULTIPLE_EMAIL.FILING_ERROR") as string;
                const errorMessage = pluralizeSentence(errorMessageTemplate, "EMAIL", numberOfEmailsToFile, translate);
                toastMessage = errorMessage
                    .replace("[[count]]", `${failedIds.length.toString()}`)
                    .replace("[[total]]", numberOfEmailsToFile.toString());

                onShowToast({ message: toastMessage }, MessageBarType.error);
                return;
            }

            const messageTemplate = translate("FILE_MULTIPLE_EMAIL.FILING_SUCCESS") as string;
            const message = pluralizeSentence(messageTemplate, "EMAIL", numberOfEmailsToFile, translate);
            toastMessage = message
                .replace("[[count]]", `${numberOfEmailsToFile.toString()}`)
                .replace("[[projectName]]", `${projectName}`);

            onShowToast({ message: toastMessage }, MessageBarType.success);
        },
        [translate, onShowToast]
    );

    const deleteFiledEmails = useCallback(
        async (emailsToDeleteIds: string[]) => {
            const emailsCountBeforeDeletion = emails.length;

            // Move current email to end of deletion queue to prevent add-in closure
            // (Unpinned add-ins close when the current email is deleted)
            const itemsToDelete = moveItemToEnd(emailsToDeleteIds, currentEmailItem?.id ?? "");

            // Process emails sequentially to avoid Graph API rate limiting (429 errors)
            for (const deletedEmailId of itemsToDelete) {
                try {
                    await msGraphApiService.moveMessageToDeletedItems(deletedEmailId);

                    setEmails((prevEmails) => prevEmails.filter((email) => deletedEmailId !== email.id));
                    setLoadedEmails((prevLoadedEmails) =>
                        prevLoadedEmails.filter((email) => deletedEmailId !== email.id)
                    );
                } catch (e) {
                    logger.error(`Error while moving email with ID ${deletedEmailId} to bin`);
                }
            }

            // Preload next batch of emails when remaining items drop below threshold
            // Prevents empty list UI after bulk operations (deletion/filing)
            // Note: we can't replace emailsCount with emails.length here because setEmails
            // used to delete emails is asynchronous and may not reflect the latest state immediately.
            if (emailsCountBeforeDeletion - emailsToDeleteIds.length < 50) {
                await loadEmails();
            }
        },
        [emails, currentEmailItem, msGraphApiService, logger, loadEmails]
    );

    const onFormSubmit = useCallback(async (): Promise<void> => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });

        let failedIds: string[] = [];
        const selectedEmailIds = selectedEmailsIds(emails);

        setIsFiling(true);
        showProgress(getFilingMessage(emails));

        try {
            failedIds = await emailApiService.fileMultipleEmails(
                selectedEmailIds,
                selectedProject as ITag,
                selectedHub as IHub,
                supportedAddinItems
            );
        } catch (error) {
            logger.error("failed filing multiple emails");
            handleApiError(error, "FILE_MULTIPLE_EMAIL.ERROR_FILING_EMAIL_GENERIC");
        }

        const successfulIds = new Set(selectedEmailIds.filter((id) => !failedIds.includes(id)));

        // set isFiled to true for all successfully filed emails in state "emails"
        setEmails((prevEmails) =>
            prevEmails.map((email) => (successfulIds.has(email.id) ? { ...email, isFiled: true } : email))
        );

        // set isFiled to true for all successfully filed emails in state "loadedEmails"
        setLoadedEmails((prevLoadedEmails) =>
            prevLoadedEmails.map((email) => (successfulIds.has(email.id) ? { ...email, isFiled: true } : email))
        );

        if (deleteEmailAfterFiling) {
            await deleteFiledEmails(Array.from(successfulIds));
        }

        showToastMessage(failedIds, selectedEmailIds.length, selectedProject?.name as string);
        showProgress(null);
        setIsFiling(false);
    }, [
        emailApiService,
        showProgress,
        deleteEmailAfterFiling,
        logger,
        emails,
        selectedProject,
        selectedHub,
        supportedAddinItems,
        selectedEmailsIds,
        handleApiError,
        showToastMessage,
        deleteFiledEmails,
    ]);

    return {
        actions: {
            onSelectAllChanged,
            loadEmails,
            onEmailSearchClear,
            onEmailSearch,
            onEmailSelectionStateChanged,
            onFormSubmit,
        },
        states: {
            isLoadingEmails,
            isLoadingMoreEmails,
            isLoadingFolderName,
            isFiling,
            loadedEmails,
            emails,
            emailOffsetToken,
            searchTerm,
            folderName,
            folderId,
            currentEmailItem,
        },
    };
};

export default useFileEmails;
